import React, { useEffect, useState } from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const INPUT_FORMATS = ["yyyy-MM-dd", "MM/dd/yyyy", "M/d/yyyy", "MM-dd-yyyy", "MMMM d, yyyy", "MMM d, yyyy"];

function tryParse(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return null;
  for (const f of INPUT_FORMATS) {
    const d = parse(trimmed, f, new Date());
    if (isValid(d)) return d;
  }
  return null;
}

/**
 * Typable neumorphic date picker.
 * value: "YYYY-MM-DD" string (or empty)
 * onChange: (str) => void
 */
export default function DatePicker({ value, onChange, max, testId, placeholder = "MM/DD/YYYY" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() =>
    value ? format(parse(value, "yyyy-MM-dd", new Date()), "MM/dd/yyyy") : "",
  );

  // Sync when parent value changes (duplicate, external clear, etc.)
  useEffect(() => {
    if (!value) {
      setText("");
    } else {
      const d = parse(value, "yyyy-MM-dd", new Date());
      if (isValid(d)) setText(format(d, "MM/dd/yyyy"));
    }
  }, [value]);

  const selected = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const maxDate = max ? parse(max, "yyyy-MM-dd", new Date()) : undefined;

  const commit = () => {
    if (!text.trim()) {
      onChange("");
      return;
    }
    const d = tryParse(text);
    if (d) {
      if (maxDate && d > maxDate) return; // silently ignore future dates when max set
      onChange(format(d, "yyyy-MM-dd"));
      setText(format(d, "MM/dd/yyyy"));
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-neu-accent hover:opacity-80"
          aria-label="Open calendar"
          data-testid={`${testId}-calendar-btn`}
        >
          <CalendarIcon className="w-4 h-4" />
        </button>
        <input
          type="text"
          inputMode="numeric"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
          }}
          placeholder={placeholder}
          className="neu-input pl-10"
          data-testid={testId}
          autoComplete="off"
        />
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <span className="absolute inset-0 pointer-events-none" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="p-2 bg-neu-surface border-none shadow-neu rounded-2xl"
        >
          <Calendar
            mode="single"
            selected={isValid(selected) ? selected : undefined}
            onSelect={(d) => {
              if (!d) return;
              onChange(format(d, "yyyy-MM-dd"));
              setText(format(d, "MM/dd/yyyy"));
              setOpen(false);
            }}
            disabled={maxDate ? { after: maxDate } : undefined}
            classNames={{
              caption_label: "text-sm font-semibold text-[#3F4E5B] font-heading",
              nav_button:
                "h-8 w-8 rounded-lg bg-neu-bg text-neu-text hover:text-neu-accent shadow-neu-inset-sm inline-flex items-center justify-center transition-colors",
              head_cell: "text-neu-muted w-9 font-medium text-[0.7rem] uppercase",
              cell: "relative p-0 text-center text-sm w-9 h-9",
              day: "h-9 w-9 p-0 font-normal rounded-lg text-neu-text hover:bg-neu-bg hover:text-neu-accent transition-colors",
              day_selected:
                "bg-neu-accent text-white hover:bg-neu-accent-dark hover:text-white shadow-md shadow-neu-accent/40",
              day_today: "text-neu-accent font-semibold",
              day_disabled: "text-neu-muted opacity-40",
              day_outside: "text-neu-muted opacity-50",
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
